import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddNewCoachPackage from './AddNewCoachPackage';
import CoachServicePackageCard from './CoachServicePackageCard';
import CoachServicePackageEditCard, {
  ICoachServicePackageCardProps,
} from './CoachServicePackageEditCard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  ICoachServicePackage,
  createCoachServicePackageRequest,
  deleteCoachServicePackageRequest,
  getCoachServicePackagesRequest,
  resetCoachServicePackages,
  updateCoachServicePackageRequest,
} from '../../redux/coach/coachSlice';

export default function CoachServices({ activeStep }: { activeStep: number }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const {
    coachServicePackages: services,
    addingNewPackageSuccess,
    updatingNewPackageSuccess,
    coachServicePackagesLoading,
  } = useAppSelector((state) => state.coach);
  const [packages, setPackages] = useState<ICoachServicePackage[]>([]);
  const [isAddingNewPackage, setIsAddingNewPackage] = useState(false);
  const [editModePackageId, setEditModePackageId] = useState<string | null>(null);
  const [deleteModePackageId, setDeleteModePackageId] = useState<string | null | undefined>(null);

  useEffect(() => {
    setPackages(services);
  }, [services]);

  useEffect(() => {
    if (coachId) {
      if (services.length === 0) dispatch(getCoachServicePackagesRequest({ coachId }));
    }
  }, [coachId]);

  useEffect(() => {
    if (addingNewPackageSuccess || updatingNewPackageSuccess) {
      if (addingNewPackageSuccess) {
        setIsAddingNewPackage(false);
      }
      if (updatingNewPackageSuccess) {
        setEditModePackageId(null);
      }
      dispatch(resetCoachServicePackages());
    }
  }, [addingNewPackageSuccess, updatingNewPackageSuccess]);

  const handleAddNewPackageClick = () => {
    setIsAddingNewPackage(true);
    setEditModePackageId(null);
    setDeleteModePackageId(null);
  };

  const handleEditPackage = (pkg: ICoachServicePackageCardProps) => {
    if (pkg.id) {
      setEditModePackageId(pkg.id);
      setIsAddingNewPackage(false);
      setDeleteModePackageId(null);
    }
  };

  const handleUpdatePackage = (pkg: ICoachServicePackageCardProps) => {
    if (pkg.id) {
      const updatedPackage = {
        id: pkg.id,
        title: pkg.title,
        bookingType: pkg.bookingType,
        sessionPrice: pkg.rate,
        sessionDuration: pkg.duration,
        sessionDescription: pkg.description,
        coachId: pkg.coachId,
        activeStep,
      };
      dispatch(updateCoachServicePackageRequest(updatedPackage));
    }
  };

  const addPackage = (newPackage: ICoachServicePackageCardProps) => {
    const newConvertedPackage = {
      title: newPackage.title,
      bookingType: newPackage.bookingType,
      sessionPrice: newPackage.rate,
      sessionDuration: newPackage.duration,
      sessionDescription: newPackage.description,
      coachId: newPackage.coachId,
      activeStep,
    };
    dispatch(createCoachServicePackageRequest(newConvertedPackage));
  };

  const handleDelete = (id: string) => {
    dispatch(deleteCoachServicePackageRequest({ id, coachId }));
  };

  useEffect(
    () => () => {
      dispatch(resetCoachServicePackages());
    },
    []
  );

  if (coachServicePackagesLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
        <Box py={1}>
          <Typography variant="title1Bold" sx={{ textAlign: 'center', color: '#2A3C67' }}>
            {t('bookings.coachServicePackages.title')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
      <Box py={1}>
        <Typography variant="title1Bold" sx={{ textAlign: 'center', color: '#2A3C67' }}>
          {t('bookings.coachServicePackages.title')}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: '20px',
          overflowX: 'auto',
          overflowY: 'auto',
          padding: '30px 20px 20px 20px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#6CA3AC',
            borderRadius: '10px',
            border: '2px solid transparent',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#4C7A83',
          },
          '&::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#365A5F',
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
          },
        }}
        justifyContent={services.length > 1 ? 'flex-start' : 'center'}
        m="auto"
      >
        {isAddingNewPackage ? (
          <CoachServicePackageEditCard
            addPackage={addPackage}
            initialPackage={null}
            onDismiss={() => setIsAddingNewPackage(false)}
          />
        ) : (
          <AddNewCoachPackage onClick={handleAddNewPackageClick} height="500px" />
        )}
        {packages.map((pkg) =>
          pkg.id === editModePackageId ? (
            <CoachServicePackageEditCard
              key={pkg.id}
              initialPackage={{
                ...pkg,
                title: pkg.title,
                bookingType: pkg.bookingType,
                rate: pkg.sessionPrice,
                duration: pkg.sessionDuration,
                description: pkg.sessionDescription,
              }}
              updatePackage={handleUpdatePackage}
              onDismiss={() => setEditModePackageId(null)}
            />
          ) : (
            <CoachServicePackageCard
              key={pkg.id}
              {...pkg}
              title={pkg.title}
              bookingType={pkg.bookingType}
              rate={pkg.sessionPrice}
              duration={pkg.sessionDuration}
              description={pkg.sessionDescription}
              onEdit={() =>
                handleEditPackage({
                  ...pkg,
                  title: pkg.title,
                  bookingType: pkg.bookingType,
                  rate: pkg.sessionPrice,
                  duration: pkg.sessionDuration,
                  description: pkg.sessionDescription,
                })
              }
              onDelete={() => handleDelete(pkg.id ?? '')}
              setDeleteModePackageId={setDeleteModePackageId}
              deleteModePackageId={deleteModePackageId}
              setEditModePackageId={setEditModePackageId}
              editModePackageId={editModePackageId}
            />
          )
        )}
      </Box>
    </Box>
  );
}
